import Microgame from '../engine/Microgame.js';

export default class PlantGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Plante !`, en: `Plant!`, es: `¡Planta!` },
            controls: { fr: `Maintiens enfoncé pour arroser la plante`, en: `Hold down to water the plant`, es: `Mantén presionado para regar la planta` },
            objective: { fr: `Fais pousser la plante jusqu'à la ligne cible`, en: `Grow the plant up to the target line`, es: `Haz crecer la planta hasta la línea objetivo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M11 20a5.96 5.96 0 0 0 3-7V9"/><path d="M7 20a5.96 5.96 0 0 1-3-7V9"/><path d="M12 20V9"/><path d="M12 4v5"/><path d="M8 8a4 4 0 0 1-4-4 4 4 0 0 1 4 4"/><path d="M16 8a4 4 0 0 0 4-4 4 4 0 0 0-4 4"/></svg>`,
            actions: [
                { type: 'hold', x: 72, y: 18, holdDuration: 1800 },
                { type: 'wait', duration: 900 },
            ],
            loopDuration: 3200
        };
    }
    constructor(engine) {
        super(engine);
        this.canvas = null;
        this.ctx = null;
        this.growth = 0;
        this.targetGrowth = 150;
        this.isPouring = false;
        this.droplets = [];
        this.leaves = [];
        this.bloomed = false;

        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.35;
        const realDurationMs = 3500 / adj;
        this.duration = realDurationMs * speedMult;

        // Joueur demande 1.2s (1200ms) de marge en x1, et 300ms en x4
        const marginMs = Math.max(300, 1200 - (speedMult - 1) * 300);
        const requiredHoldTimeMs = realDurationMs - marginMs;
        
        // Calculate the exact growth per frame (16.67ms) to reach targetGrowth in requiredHoldTimeMs
        const framesRequired = Math.max(1, requiredHoldTimeMs / 16.67);
        this.growSpeed = this.targetGrowth / framesRequired;

        this._isAnimating = false;
        this._frozenTimerValue = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "WATER!";

        this.canvas = document.createElement('canvas');
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.width = '100%';
        this.canvas.style.height = '100%';

        const rect = this.container.getBoundingClientRect();
        this.W = rect.width || 400;
        this.H = rect.height || 700;
        this.canvas.width = this.W;
        this.canvas.height = this.H;
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        this.groundY = this.H * 0.78;
        this.stemX = this.W * 0.5;
        this.canX = this.W * 0.72;
        this.canY = this.H * 0.18;

        // Precompute leaf positions (random but fixed per game)
        this.leafPositions = [];
        for (let i = 0; i < 4; i++) {
            this.leafPositions.push({
                heightPct: this.isPlayback ? (0.27 + i * 0.17) : (0.25 + i * 0.18 + Math.random() * 0.05),
                side: i % 2 === 0 ? -1 : 1,
                size: this.isPlayback ? (15 + i) : (12 + Math.random() * 8)
            });
        }

        // Bloom petals
        this.petalCount = 8;
        this.bloomProgress = 0;

        this.onDown = this.onDown.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointerup', this.onUp);

        this.lastTime = null;
        this.animLoop();
    }

    update(dt) {
        if (this._isAnimating) {
            this.engine.timer = Math.max(0, this._frozenTimerValue);
        }
    }

    animLoop() {
        if (!this.active) return;

        const now = performance.now();
        if (!this.lastTime) this.lastTime = now;
        const dt = Math.min((now - this.lastTime) / 16.67, 3);
        this.lastTime = now;

        if (this.isPouring && !this.bloomed) {
            this.growth += this.growSpeed * dt;

            // Spawn droplets
            if (Math.random() < 0.4 * dt) {
                this.droplets.push({
                    x: this.canX - 30 + (Math.random() - 0.5) * 10,
                    y: this.canY + 40,
                    vx: -0.8 + Math.random() * 0.3,
                    vy: 2 + Math.random(),
                    life: 0,
                    size: 3 + Math.random() * 4
                });
            }

            if (this.growth >= this.targetGrowth && !this.bloomed) {
                this.bloomed = true;
                this.bloomStart = now;
                this._isAnimating = true;
                this._frozenTimerValue = this.engine.timer;
                setTimeout(() => this.win(), 900);
            }
        }

        // Update droplets
        for (let i = this.droplets.length - 1; i >= 0; i--) {
            const d = this.droplets[i];
            d.x += d.vx * dt;
            d.vy += 0.15 * dt;
            d.y += d.vy * dt;
            d.life += dt;
            if (d.y > this.groundY || d.life > 40) {
                this.droplets.splice(i, 1);
            }
        }

        // Bloom progress
        if (this.bloomed) {
            this.bloomProgress = Math.min(1, (now - this.bloomStart) / 700);
        }

        this.draw();
        requestAnimationFrame(() => this.animLoop());
    }

    draw() {
        const ctx = this.ctx;
        ctx.clearRect(0, 0, this.W, this.H);

        // Ground
        ctx.save();
        ctx.fillStyle = this.secondaryColor;
        ctx.globalAlpha = 0.25;
        ctx.fillRect(0, this.groundY, this.W, this.H - this.groundY);
        ctx.restore();

        // Ground line
        ctx.save();
        ctx.strokeStyle = this.secondaryColor;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.6;
        ctx.beginPath();
        ctx.moveTo(0, this.groundY);
        ctx.lineTo(this.W, this.groundY);
        ctx.stroke();
        ctx.restore();

        // Pot
        const potW = 50, potH = 40;
        ctx.save();
        ctx.fillStyle = this.secondaryColor;
        ctx.globalAlpha = 0.7;
        ctx.beginPath();
        ctx.moveTo(this.stemX - potW / 2, this.groundY);
        ctx.lineTo(this.stemX - potW / 2 + 6, this.groundY - potH);
        ctx.lineTo(this.stemX + potW / 2 - 6, this.groundY - potH);
        ctx.lineTo(this.stemX + potW / 2, this.groundY);
        ctx.closePath();
        ctx.fill();

        // Pot rim
        ctx.fillRect(this.stemX - potW / 2 - 3, this.groundY - potH - 5, potW + 6, 8);
        ctx.restore();

        // Earth in pot
        ctx.save();
        ctx.fillStyle = this.darken(this.secondaryColor, 0.4);
        ctx.beginPath();
        ctx.ellipse(this.stemX, this.groundY - potH, potW / 2 - 8, 6, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // Stem
        const stemHeight = Math.min(this.growth, this.targetGrowth);
        const stemTopY = this.groundY - potH - stemHeight;
        if (stemHeight > 0) {
            ctx.save();
            ctx.strokeStyle = this.primaryColor;
            ctx.lineWidth = 5;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(this.stemX, this.groundY - potH);
            // Slight curve
            const curveX = this.stemX + Math.sin(stemHeight / 30) * 5;
            ctx.quadraticCurveTo(curveX, (this.groundY - potH + stemTopY) / 2, this.stemX, stemTopY);
            ctx.stroke();
            ctx.restore();

            // Leaves
            for (const lp of this.leafPositions) {
                const leafGrowth = Math.max(0, Math.min(1, (stemHeight / this.targetGrowth - lp.heightPct + 0.15) * 4));
                if (leafGrowth <= 0) continue;

                const ly = this.groundY - potH - stemHeight * lp.heightPct;
                const lSize = lp.size * leafGrowth;

                ctx.save();
                ctx.translate(this.stemX, ly);
                ctx.fillStyle = this.primaryColor;
                ctx.globalAlpha = 0.7;

                ctx.beginPath();
                if (lp.side < 0) {
                    ctx.ellipse(-lSize * 0.7, -2, lSize, lSize * 0.4, -0.3, 0, Math.PI * 2);
                } else {
                    ctx.ellipse(lSize * 0.7, -2, lSize, lSize * 0.4, 0.3, 0, Math.PI * 2);
                }
                ctx.fill();
                ctx.restore();
            }
        }

        // Bloom
        if (this.bloomed && this.bloomProgress > 0) {
            ctx.save();
            ctx.translate(this.stemX, stemTopY);

            for (let i = 0; i < this.petalCount; i++) {
                const angle = (i / this.petalCount) * Math.PI * 2;
                const scale = Math.min(1, this.bloomProgress * 2 - i * 0.08);
                if (scale <= 0) continue;

                ctx.save();
                ctx.rotate(angle);
                ctx.fillStyle = this.primaryColor;
                ctx.globalAlpha = 0.9;
                ctx.beginPath();
                ctx.ellipse(0, -18 * scale, 10 * scale, 22 * scale, 0, 0, Math.PI * 2);
                ctx.fill();

                // Petal glow
                ctx.globalAlpha = 0.3;
                ctx.shadowColor = this.primaryColor;
                ctx.shadowBlur = 10;
                ctx.fill();
                ctx.restore();
            }

            // Center
            const centerScale = Math.max(0, this.bloomProgress * 1.5 - 0.5);
            if (centerScale > 0) {
                ctx.fillStyle = this.secondaryColor;
                ctx.globalAlpha = 1;
                ctx.beginPath();
                ctx.arc(0, 0, 8 * centerScale, 0, Math.PI * 2);
                ctx.fill();

                ctx.globalAlpha = 0.4;
                ctx.shadowColor = this.secondaryColor;
                ctx.shadowBlur = 12;
                ctx.fill();
            }

            ctx.restore();
        }

        // Watering can
        ctx.save();
        ctx.translate(this.canX, this.canY);
        const canAngle = this.isPouring ? -0.7 : 0;
        ctx.rotate(canAngle);

        // Can body
        ctx.strokeStyle = this.secondaryColor;
        ctx.lineWidth = 3;
        ctx.globalAlpha = 0.8;
        ctx.beginPath();
        ctx.roundRect(-35, -20, 70, 45, 8);
        ctx.stroke();

        // Handle
        ctx.beginPath();
        ctx.arc(15, -28, 18, Math.PI, 0);
        ctx.stroke();

        // Spout
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(-35, -10);
        ctx.lineTo(-60, -30);
        ctx.stroke();

        // Spout dots (shower head)
        ctx.fillStyle = this.secondaryColor;
        for (let i = 0; i < 3; i++) {
            ctx.beginPath();
            ctx.arc(-60 - i * 4, -32 + i * 2, 2, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();

        // Droplets
        ctx.save();
        ctx.fillStyle = this.secondaryColor;
        for (const d of this.droplets) {
            ctx.globalAlpha = Math.max(0, 0.7 - d.life / 40);
            ctx.beginPath();
            ctx.arc(d.x, d.y, d.size, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    }

    darken(hex, factor) {
        const c = document.createElement('canvas').getContext('2d');
        c.fillStyle = hex;
        const match = c.fillStyle.match(/\d+/g);
        if (!match) return hex;
        return `rgb(${Math.floor(match[0] * factor)},${Math.floor(match[1] * factor)},${Math.floor(match[2] * factor)})`;
    }

    onDown() {
        if (this.won || this.lost) return;
        this.isPouring = true;
    }

    onUp() {
        this.isPouring = false;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
